import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Trophy } from "lucide-react";

const EMOJIS = ["🐒", "🍌", "⭐", "🌈", "🎈", "🦁", "🐘", "🐟"];

function buildDeck(pairCount = 6) {
  const picks = EMOJIS.slice(0, pairCount);
  const deck = [...picks, ...picks].map((emoji, i) => ({
    id: i + "-" + Math.random().toString(36).slice(2, 7),
    emoji,
    flipped: false,
    matched: false,
  }));
  return deck.sort(() => Math.random() - 0.5);
}

export default function MemoryGame() {
  const [pairs] = useState(6);
  const [deck, setDeck] = useState(() => buildDeck(pairs));
  const [selected, setSelected] = useState([]);
  const [moves, setMoves] = useState(0);
  const [locked, setLocked] = useState(false);

  const allMatched = useMemo(() => deck.every((c) => c.matched), [deck]);

  const reset = () => {
    setDeck(buildDeck(pairs));
    setSelected([]);
    setMoves(0);
    setLocked(false);
  };

  const flip = (id) => {
    if (locked) return;
    setDeck((d) => d.map((c) => (c.id === id && !c.flipped && !c.matched ? { ...c, flipped: true } : c)));
    setSelected((s) => (s.length < 2 ? [...s, id] : s));
  };

  useEffect(() => {
    if (selected.length !== 2) return;
    setLocked(true);
    setMoves((m) => m + 1);
    const [a, b] = selected;
    const cardA = deck.find((c) => c.id === a);
    const cardB = deck.find((c) => c.id === b);
    if (cardA && cardB && cardA.emoji === cardB.emoji) {
      setTimeout(() => {
        setDeck((d) => d.map((c) => (c.id === a || c.id === b ? { ...c, matched: true } : c)));
        setSelected([]);
        setLocked(false);
      }, 500);
    } else {
      setTimeout(() => {
        setDeck((d) => d.map((c) => (c.id === a || c.id === b ? { ...c, flipped: false } : c)));
        setSelected([]);
        setLocked(false);
      }, 900);
    }
  }, [selected, deck]);

  return (
    <main className="mx-auto max-w-3xl px-6 py-10" data-testid="memory-game">
      <div className="flex items-center justify-between">
        <Link to="/games" className="inline-flex items-center gap-2 text-brand-ink/80 hover:text-brand-red font-bold">
          <ArrowLeft size={18} /> All games
        </Link>
        <div className="flex items-center gap-2">
          <div className="rounded-full border-2 border-brand-ink bg-white px-4 py-1.5 font-bold text-sm shadow-brutal-sm" data-testid="memory-moves">
            Moves: {moves}
          </div>
          <button
            onClick={reset}
            data-testid="memory-reset"
            className="rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-1.5 font-bold text-sm shadow-brutal-sm inline-flex items-center gap-1 hover:-translate-y-0.5 transition-transform"
          >
            <RefreshCw size={14} /> Reset
          </button>
        </div>
      </div>

      <h1 className="mt-4 font-display text-4xl md:text-5xl font-bold text-brand-ink">Memory Pairs</h1>
      <p className="mt-2 text-brand-ink/80 text-lg">Tap two cards. Find the matching pair!</p>

      <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="memory-board">
        {deck.map((c) => (
          <button
            key={c.id}
            onClick={() => flip(c.id)}
            disabled={c.matched}
            data-testid={`memory-card-${c.id}`}
            className={`flip-card aspect-square w-full ${c.flipped || c.matched ? "flipped" : ""}`}
          >
            <div className="flip-card-inner h-full w-full">
              <div className="flip-face bg-brand-red border-[3px] border-brand-ink shadow-brutal-sm">
                <span className="font-display text-white text-4xl">?</span>
              </div>
              <div className={`flip-face flip-back border-[3px] border-brand-ink shadow-brutal-sm ${c.matched ? "bg-brand-green" : "bg-white"}`}>
                <span className="text-4xl md:text-5xl">{c.emoji}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {allMatched && (
        <div className="mt-8 rounded-3xl border-[3px] border-brand-ink bg-brand-green text-white p-6 shadow-brutal text-center" data-testid="memory-win">
          <Trophy size={40} className="mx-auto text-brand-yellow" fill="#FFD166" />
          <h2 className="mt-2 font-display text-3xl font-bold">Well done! 🎉</h2>
          <p className="mt-1">You found all the pairs in <strong>{moves}</strong> moves.</p>
          <button
            onClick={reset}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-brand-ink font-bold px-5 py-2 btn-brutal"
            data-testid="memory-play-again"
          >
            <RefreshCw size={16} /> Play again
          </button>
        </div>
      )}
    </main>
  );
}
